import AppCommandBar from 'app/AppCommandBar'
import { AutoStack, FlexSpinner } from 'components'
import { useDeleteBlobMutation } from 'components/DocumentGenerator/documentGenerator.generated'
import { RichTextViewer } from 'components/RichTextViewer'
import { FormikActions } from 'formik'
import {
  ParticipantDetailsForm,
  ParticipantDetailsFormSchema,
} from 'forms/ProvingDetailsForm/participants/ParticipantDetailsForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import {
  canUpdateProving,
  clearObjectIdsByPath,
  isInProvingRole,
  omitDeep,
  removeNullProperties,
} from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand, useDeselectCommand } from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useSelection } from 'hooks/useSelection'
import {
  FileTemplateType,
  FileType,
  GeneratedDocument,
  GeneratedDocumentInputType,
  Participant,
  ParticipantBatchInput,
  ProvingUserRole,
  TrackHotel,
  UserRole,
} from 'model'
import { IconButton, PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as path from 'path'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useGenerateFileFromTemplateMutation } from 'views/AdminFileTemplatesView/AdminFileTemplatesView.generated'
import { Page, Section } from './Layout'
import {
  useUpdateParticipantsMutation,
  useUpdateProvingMutation,
} from './hooks/index'
import { FormList } from './lists/index'

enum FormType {
  qualifications,
  annotation,
}

export const PageForms: React.FC = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = React.useState(false)
  const [proving, refetch] = useCurrentProving()
  const [updateParticipants] = useUpdateParticipantsMutation()
  const [deleteBlob] = useDeleteBlobMutation()
  const [generateDocument] = useGenerateFileFromTemplateMutation()
  const [editPanelState, showEditPanel, hideEditPanel] = useFormikPanelState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [updateProving] = useUpdateProvingMutation()
  const [formType, setFormType] = React.useState()
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  let canUpdateCurrentProving = canUpdateProving(proving)
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const canUseCommand = canUpdateCurrentProving && !isMaintenanceHead
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()

  const templateTypes = [
    'temporaryPass',
    'passActivation',
    'permanentDriveAuthorization',
    'singleDriveAuthorization',
    'dvEquipment',
    'partsTransport',
    'driverTraining',
  ] as FileTemplateType[]

  const templates =
    proving.provingGround && proving.provingGround.fileTemplates
      ? proving.provingGround.fileTemplates.filter((ft) =>
          templateTypes.includes(ft.type)
        )
      : []

  const [selection, selectedItems] = useSelection<Participant>({
    getKey: (p) => p._id,
  })
  const participants = proving.participants || []

  const handleGenerateForms = React.useCallback(
    async (typeToGenerate?: FileTemplateType) => {
      setLoading(true)

      const typesToGenerate = typeToGenerate ? [typeToGenerate] : templateTypes
      try {
        const batches: ParticipantBatchInput[] = []
        // go through all selected participants
        for (const participant of selectedItems) {
          if (!participant.forms) {
            participant.forms = {}
          }
          const batch: ParticipantBatchInput = {
            _id: participant._id,
            update: {
              forms: {},
            },
          }
          let createBatch = false

          // go through all form types
          for (const type of typesToGenerate) {
            const form: GeneratedDocument = participant.forms![type]

            // check if the respective boolean in the participant is set
            if (!participant[type]) {
              continue
            } else {
              createBatch = true
            }

            // see if form has been created, if so delete it
            if (form) {
              await deleteBlob({
                variables: {
                  uri: form.uri,
                },
              })
            }

            // find the correct template
            const fileTemplate = templates.find((tmpl) => tmpl.type === type)

            if (!fileTemplate) {
              // no fileTemplate to be found...
              createBatch = false
              continue
            }

            // generate the document
            const { data: docData } = await generateDocument({
              variables: {
                provingId: proving._id,
                mapping: fileTemplate.cellMapping || '{}',
                templateUri: fileTemplate.uri,
                isTestDoc: false,
              },
            })
            if (!docData) {
              throw new Error('Document was not correctly generated!')
            }
            const doc = docData.generateFileFromTemplate

            batch.update.forms![type] = {
              name: doc.name,
              uri: doc.uri,
              type: getFileType(doc.name),
            } as GeneratedDocumentInputType
          }

          if (createBatch) {
            batches.push(batch)
          }
        }

        if (batches !== []) {
          // update all parcicipants at once
          await updateParticipants({
            variables: {
              provingId: proving._id,
              batch: batches,
            },
          })
        }
      } catch (err) {
        throw err
      } finally {
        await refetch()
        setLoading(false)
      }
    },
    [
      proving,
      selectedItems,
      deleteBlob,
      generateDocument,
      refetch,
      templateTypes,
      templates,
      updateParticipants,
    ]
  )

  const handleFormToggle = React.useCallback(
    async (formTemplateType: string) => {
      setLoading(true)
      const batch: ParticipantBatchInput[] = selectedItems.map(
        (participant) => {
          const b = {
            _id: participant._id,
            update: {},
          }
          b.update[formTemplateType] = !participant[formTemplateType]
          return b
        }
      )

      try {
        await updateParticipants({
          variables: {
            batch,
            provingId: proving._id,
          },
        })
      } catch (err) {
        throw err
      } finally {
        await refetch()
        setLoading(false)
      }
    },
    [proving, selectedItems, updateParticipants, refetch]
  )

  useCommand(
    {
      key: 'forms',
      priority: 10,
      text: t('model.proving.form.assignForms'),
      iconProps: { iconName: 'TextDocument' },
      disabled: selectedItems.length < 1 || !canUseCommand,
      subMenuProps: {
        items: templateTypes.map((templType) => ({
          key: `set_${templType}`,
          text: t(`field.${templType}.labelVerbose`),
          onClick: () => {
            setFormType({ formType: FormType.qualifications })
            handleFormToggle(templType)
          },
        })),
      },
    },
    [selectedItems]
  )

  useCommand(
    {
      key: 'generateForms',
      priority: 20,
      text: t('model.proving.form.generateForms'),
      iconProps: { iconName: 'pageAdd' },
      disabled: selectedItems.length < 1 || !canUseCommand,
      subMenuProps: {
        items: [
          {
            key: 'generateAll',
            text: t('model.proving.form.createAllForms'),
            onClick: () => {
              handleGenerateForms()
            },
          },
          ...templateTypes.map((type) => ({
            key: `generate_${type}`,
            text: t(`field.${type}.labelGenerate`),
            onClick: () => {
              handleGenerateForms(type)
            },
          })),
        ],
      },
    },
    [proving, selectedItems]
  )
  // useCommand({
  //   key: 'generateAndPrintForms',
  //   disabled: true,
  //   priority: 30,
  //   text: t('model.proving.form.generateAndPrintForms'),
  //   iconProps: { iconName: 'printfaxPrinterFile' },
  // })
  // useCommand({
  //   key: 'printForms',
  //   disabled: true,
  //   priority: 40,
  //   text: t('model.proving.form.printForms'),
  //   iconProps: { iconName: 'print' },
  // })
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 20,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  const onItemInvoked = (item: any): void => {
    setSelectedItemId(item._id)
    showEditPanel({
      ...item,
      flight: {
        ...item.flight,
        note: item.flight.note || '',
        outboundData: {
          ...item.flight.outboundData,
          airline: item.flight.outboundData?.airline
            ? item.flight.outboundData.airline
            : '',
          flightNumber: item.flight.outboundData?.flightNumber
            ? item.flight.outboundData.flightNumber
            : '',
        },
        returnData: {
          ...item.flight.returnData,
          airline: item.flight.returnData?.airline
            ? item.flight.returnData.airline
            : '',
          flightNumber: item.flight.returnData?.flightNumber
            ? item.flight.returnData.flightNumber
            : '',
        },
      },
      accomodation: {
        ...item.accomodation,
        accomodation: item.accomodation.accomodation
          ? item.accomodation.accomodation._id
          : '',
      },
      trackHotels: {
        outward: item.trackHotels.outward.map((out: TrackHotel) => ({
          ...out,
          hotel: out.hotel ? out.hotel._id : null,
        })),
        return: item.trackHotels.return.map((ret: TrackHotel) => ({
          ...ret,
          hotel: ret.hotel ? ret.hotel._id : null,
        })),
        ...item.trackHotel,
      },
      ferry: {
        ...item.ferry,
        outward: item.ferry.outward ? item.ferry.outward._id : null,
        return: item.ferry.return ? item.ferry.return._id : null,
      },
    })
  }

  const handleDetailsViewSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)

      values = clearObjectIdsByPath(values, [
        'accomodation.accomodation',
        'ferry.outward',
        'ferry.return',
      ])

      const updateValues = removeNullProperties({
        orderId: values.orderId,
        dateTo: values.dateTo,
        dateFrom: values.dateFrom,
        note: values.note,
        percentage: values.percentage,
        flight: values.flight,
        accomodation: values.accomodation,
        trackHotelRequired: values.trackHotelRequired,
        trackHotels: values.trackHotels,
        rentalCar: values.rentalCar,
        group: values.group,
        temporaryPass: values.temporaryPass,
        passActivation: values.passActivation,
        permanentDriveAuthorization: values.permanentDriveAuthorization,
        singleDriveAuthorization: values.singleDriveAuthorization,
        dvEquipment: values.dvEquipment,
        partsTransport: values.partsTransport,
        driverTraining: values.driverTraining,
        ferry: values.ferry,
        nordschleife: values.nordschleife,
      })

      const { errors } = await updateParticipants({
        variables: {
          provingId: proving._id,
          batch: [
            {
              _id: selectedItemId,
              update: omitDeep(updateValues, ['__typename']),
            },
          ],
        },
      })

      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideEditPanel()
    },
    [
      hideEditPanel,
      proving,
      updateParticipants,
      selectedItemId,
      refetch,
      selection,
    ]
  )

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationForms: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  return (
    <Page>
      <Section label={t('section.forms.annotation')}>
        <AutoStack horizontal={true}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            disabled={!canUseCommand}
            onClick={() => {
              setFormType({ formType: FormType.annotation })
              showAnnotationPanel({
                annotation: proving.annotationForms || '',
              })
            }}
          />
          <RichTextViewer
            richText={proving.annotationForms ? proving.annotationForms : ''}
          />
        </AutoStack>
      </Section>
      <Section label={t('section.forms.header')}>
        <FlexSpinner loading={loading}>
          <AppCommandBar />
          <FormList
            items={participants}
            selection={selection}
            onItemInvoked={onItemInvoked}
          />
          <FormikPanel
            {...editPanelState}
            onSubmit={handleDetailsViewSubmit}
            type={PanelType.large}
            validationSchema={
              new ParticipantDetailsFormSchema(
                proving.dateFrom as string,
                proving.dateTo as string
              )
            }
          >
            {formType !== undefined &&
              formType.formType === FormType.qualifications && (
                <ParticipantDetailsForm />
              )}
          </FormikPanel>
          <FormikPanel
            {...annotationPanelState}
            onSubmit={handleAnnotationSubmit}
          >
            {formType !== undefined &&
              formType.formType === FormType.annotation && (
                <ProvingDetailsAnnotationForm />
              )}
          </FormikPanel>
        </FlexSpinner>
      </Section>
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}

function getFileType(fileName: string) {
  const ext = path.extname(fileName)
  switch (ext) {
    case '.docx': {
      return FileType.Word
    }
    case '.xlsx': {
      return FileType.Excel
    }
    default:
      return undefined
  }
}
